/**
 * conversion-dashboard-shared.tsx — 转化类看板共享件（付费转化看板 S012 / 激活转化看板 S013）
 *
 * 日期窗换算（本地日历日 → UTC 日串）、CSV 字段转义与下载、维度 / 指标切换芯片、趋势配色。
 * 两个看板筛选与三视图结构相同，只在指标与维度上不同，故公共件放这里避免逐页漂移。
 */

import type { ReactNode } from 'react';

export const SERIES_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
];

/** 本地日历日 → YYYY-MM-DD（日期选择器给的是本地零点，按所选日历日下发，服务端按 UTC 日切） */
export function toDay(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** 默认近 30 天（含今天） */
export function defaultRange(): [Date, Date] {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return [start, end];
}

export function daysBetween(from: string, to: string): string[] {
  const days: string[] = [];
  for (
    let cursor = Date.parse(`${from}T00:00:00Z`);
    cursor <= Date.parse(`${to}T00:00:00Z`);
    cursor += 86_400_000
  )
    days.push(new Date(cursor).toISOString().slice(0, 10));
  return days;
}

/** CSV 字段转义 + 公式注入防护（维度值来自客户端上报，可能以 = + - @ 开头） */
export function csvField(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  // \r 同样要触发加引号：裸 \r 会被部分表格软件当作换行，把后续内容（含公式）挤到新单元格
  return /[",\n\r]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

/** 按行（已是单元格文本）生成 CSV 并触发下载；BOM 让 Excel 按 UTF-8 打开中文表头 */
export function downloadCsvFile(fileName: string, lines: string[][]): void {
  const body = lines.map((cells) => cells.map(csvField).join(',')).join('\n');
  const blob = new Blob([`﻿${body}\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function ToggleChip({
  on,
  label,
  title,
  disabled,
  onClick,
}: {
  on: boolean;
  label: string;
  title?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`chip${on ? ' chip-accent' : ''}`}
      aria-pressed={on}
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }}
    >
      {label}
    </button>
  );
}

export function ChipRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="hstack" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ width: 36, fontSize: 12, color: 'var(--fg-muted)', flexShrink: 0 }}>
        {label}
      </span>
      {children}
    </div>
  );
}
